import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import { FormEvent, useState } from 'react'
import writeXlsxFile, { Row, SheetData } from 'write-excel-file'
import { get } from '../../../lib/http'
import extractDataForm from '../../../utilities/extractDataForm'

import { DataForm, ResultCashInflowOutflow } from './types'

export default function usePrintCashInflowOutflow() {
    const toast = useToast()
    const [isLoading, setIsLoading] = useState(false)

    const head: (...values: string[]) => Row[number][] = (...values) =>
        values.map<Row[number]>(val => ({
            value: val,
            fontWeight: 'bold',
            wrap: true,
            alignVertical: 'center',
            align: 'left',
            fontSize: 11
        }))

    function setDataCashInflowOutflow(
        data: ResultCashInflowOutflow[],
        rows: SheetData
    ) {
        data.forEach(item => {
            rows.push([
                {
                    value: item.fecha_apertura,
                    type: String
                },
                {
                    value: item.caja,
                    type: String
                },
                {
                    value: item.usuario,
                    type: String
                },
                {
                    value: item.fecha_movimiento,
                    type: String
                },
                {
                    value: item.tipo_movimiento,
                    type: String
                },
                {
                    value: item.monto,
                    type: Number,
                    align: 'right',
                    format: '#,##0.00'
                },
                {
                    value: item.concepto,
                    type: String
                },
                {
                    value: item.comentario || '',
                    type: String
                }
            ])
        })
    }

    async function submit(ev: FormEvent<HTMLFormElement>) {
        ev.preventDefault()
        const form = extractDataForm<DataForm>(ev.target)
        const startDate = dayjs
            .tz(form.startDate + ' 00:00:00', 'America/Lima')
            .toISOString()
        const endDate = dayjs
            .tz(form.endDate + ' 23:59:59', 'America/Lima')
            .toISOString()

        const rows: SheetData = [
            [],
            [
                {
                    value: 'Reporte de Ingresos y Salidas de Efectivo',
                    fontWeight: 'bold',
                    span: 8,
                    alignVertical: 'center',
                    align: 'center',
                    fontSize: 16
                }
            ],
            [
                {
                    value: `De: ${dayjs(startDate).format(
                        'DD/MM/YYYY'
                    )} - Hasta ${dayjs(endDate).format('DD/MM/YYYY')}`,
                    span: 8,
                    align: 'center'
                }
            ],
            [],
            head(
                ...[
                    'Fecha de apertura',
                    'Caja',
                    'Usuario',
                    'Fecha de movimiento',
                    'Tipo de movimiento',
                    'Monto (S/)',
                    'Concepto',
                    'Comentario'
                ]
            )
        ]

        setIsLoading(true)

        const { data, error } = await get<ResultCashInflowOutflow[]>(
            `/api/reports/cash_inflow_outflow?start_date=${startDate}&end_date=${endDate}`
        )

        if (error) {
            toast({
                title: 'Hubo un error al cargar los datos',
                status: 'error',
                position: 'top'
            })
            return
        }

        setDataCashInflowOutflow(data.result, rows)

        await writeXlsxFile(rows, {
            fileName: `Reporte de Ingresos y Salida de Efectivo ${dayjs(
                startDate
            ).format('DD/MM/YYYY')} - ${dayjs(endDate).format(
                'DD/MM/YYYY'
            )}.xlsx`,
            columns: [20, 12, 12, 20, 18, 15, 20, 25].map(width => ({
                width
            })),
            fontSize: 10,
            fontFamily: 'Calibri'
        })

        setIsLoading(false)
    }

    return {
        submit,
        isLoading
    }
}
